import React from 'react';
import { Button, Card, Col, Collapse, Descriptions, Divider, Row, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { CheckCircleOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { useAtom } from 'jotai';
import { batchItemsAtom, confirmationModalAtom, previewDataAtom, uploadedFilesAtom } from './types';
import ProDescriptions from "@ant-design/pro-descriptions";

const { Panel } = Collapse;
const { Title, Text } = Typography;

interface VerificationStepProps {
    handleSubmit: () => Promise<boolean>;
    handleDeleteBatch: () => Promise<void>;
    fetchAllReleaseFiles: (items: any[]) => Promise<void>;
    fetchBatchPreviews: () => Promise<void>;
    fetchingExistingFiles: boolean;
    processingBatch: boolean;
}

const VerificationStep: React.FC<VerificationStepProps> = ({
                                                               handleSubmit,
                                                               handleDeleteBatch,
                                                               fetchAllReleaseFiles,
                                                               fetchBatchPreviews,
                                                               fetchingExistingFiles,
                                                               processingBatch
                                                           }) => {
    const [batchItems] = useAtom(batchItemsAtom);
    const [uploadedFiles] = useAtom(uploadedFilesAtom);
    const [previewData] = useAtom(previewDataAtom);
    const [, setConfirmationModal] = useAtom(confirmationModalAtom);

    return (
        <div style={{marginTop: 24}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                    <Title level={4}>Verify and Submit</Title>
                    <Text>Review the batch information and submit to assign identifiers and finalize the releases.</Text>
                </div>
                <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => {
                        fetchAllReleaseFiles(batchItems);
                        fetchBatchPreviews();
                    }}
                    loading={fetchingExistingFiles}
                    disabled={processingBatch}
                >
                    Refresh
                </Button>
            </div>

            {fetchingExistingFiles && (
                <div style={{ textAlign: 'center', margin: '20px 0' }}>
                    <Spin tip="Loading existing files..." />
                </div>
            )}

            {Object.keys(previewData).length > 0 && (
                <Collapse defaultActiveKey={Object.keys(previewData)} style={{marginTop: 24}}>
                    {Object.entries(previewData).map(([releaseId, releasePreview]) => {
                        // Check if there are any validation warnings
                        const validationWarnings = releasePreview.release?.validation_warnings || [];
                        const trackWarnings = releasePreview.release?.art_tracks?.flatMap(track => track.validation_warnings || []) || [];
                        const allWarnings = [...validationWarnings, ...trackWarnings];

                        return (
                            <Panel
                                key={releaseId}
                                header={
                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
                                        <Title level={5} style={{margin: 0}}>{releasePreview.release?.name || 'Unnamed Release'}</Title>
                                        {allWarnings.length > 0 && (
                                            <Tag color="warning" icon={<WarningOutlined/>}>
                                                {allWarnings.length} Warning{allWarnings.length !== 1 ? 's' : ''}
                                            </Tag>
                                        )}
                                    </div>
                                }
                            >
                                {/* Validation Warnings Section */}
                                {allWarnings.length > 0 && (
                                    <Card
                                        type="inner"
                                        title={
                                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                                <Title level={5} style={{margin: 0, color: '#faad14'}}>Validation Warnings</Title>
                                            </div>
                                        }
                                        style={{marginBottom: 16}}
                                    >
                                        <ul style={{paddingLeft: 20}}>
                                            {validationWarnings.map((warning, index) => (
                                                <li key={`release-${index}`} style={{marginBottom: 8}}>
                                                    <Text strong>Release:</Text> {warning.message} <Text type="secondary">({warning.field})</Text>
                                                </li>
                                            ))}
                                            {trackWarnings.map((warning, index) => (
                                                <li key={`track-${index}`} style={{marginBottom: 8}}>
                                                    <Text strong>Track:</Text> {warning.message} <Text type="secondary">({warning.field})</Text>
                                                </li>
                                            ))}
                                        </ul>
                                    </Card>
                                )}

                                {/* Release Details Section */}
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <ProDescriptions column={1} title="Basic Information" bordered>
                                            <ProDescriptions.Item label="Release Title">
                                                {releasePreview.release?.name || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="Version/Subtitle">
                                                {releasePreview.release?.subtitle || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="UPC/EAN">
                                                {releasePreview.release?.upc_ean ? (
                                                    <Tag color="success">{releasePreview.release.upc_ean}</Tag>
                                                ) : releasePreview.preview_upc ? (
                                                    <Tooltip title="Will be assigned on submission">
                                                        <Tag color="warning" icon={<WarningOutlined/>}>
                                                            {releasePreview.preview_upc}
                                                        </Tag>
                                                    </Tooltip>
                                                ) : (
                                                    <Text type="danger">Missing</Text>
                                                )}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="Format">
                                                {releasePreview.release?.type || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="No. of Tracks">
                                                {releasePreview.release?.art_tracks?.length || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                        </ProDescriptions>
                                    </Col>
                                    <Col span={12}>
                                        <ProDescriptions column={1} title="Artists Information" bordered>
                                            <ProDescriptions.Item label="Main Artists">
                                                {releasePreview.release?.artists?.map(artist =>
                                                    artist.names?.find(name => name.is_default)?.full_name || artist.key_name
                                                ).join(', ') || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="Featuring Artists">
                                                {releasePreview.release?.featuring_artists?.map(artist =>
                                                    artist.names?.find(name => name.is_default)?.full_name || artist.key_name
                                                ).join(', ') || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="Artist Display Name">
                                                {releasePreview.release?.artist_display || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                        </ProDescriptions>
                                    </Col>
                                    <Col span={12}>
                                        <ProDescriptions column={1} title="Label Information" bordered>
                                            <ProDescriptions.Item label="Label">
                                                {releasePreview.release?.label?.name || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="Asset Label">
                                                {releasePreview.release?.asset_label?.name || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="Genre">
                                                {releasePreview.release?.genre || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="Subgenre">
                                                {releasePreview.release?.sub_genre || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                        </ProDescriptions>
                                    </Col>
                                    <Col span={12}>
                                        <ProDescriptions column={1} title="Release Dates & Territories" bordered>
                                            <ProDescriptions.Item label="Digital Release Date">
                                                {releasePreview.release?.digital_release_date || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="Original Release Date">
                                                {releasePreview.release?.original_release_date || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="Production Year">
                                                {releasePreview.release?.production_year || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="Territories">
                                                {Array.isArray(releasePreview.release?.territories)
                                                    ? releasePreview.release?.territories.join(', ')
                                                    : releasePreview.release?.territories || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                            <ProDescriptions.Item label="Excluded Territories">
                                                {Array.isArray(releasePreview.release?.excluded_territories)
                                                    ? releasePreview.release?.excluded_territories.join(', ')
                                                    : releasePreview.release?.excluded_territories || <Text type="secondary">N/A</Text>}
                                            </ProDescriptions.Item>
                                        </ProDescriptions>
                                    </Col>
                                    <Col span={24}>
                                        <Row gutter={[16, 0]}>
                                            <Col span={8}>
                                                <ProDescriptions column={1} title="Additional Information" bordered>
                                                    <ProDescriptions.Item label="Price Tier">
                                                        {releasePreview.release?.price_tier || <Text type="secondary">N/A</Text>}
                                                    </ProDescriptions.Item>
                                                    <ProDescriptions.Item label="Catalogue Number">
                                                        {releasePreview.release?.release_catalogue_number || <Text type="secondary">N/A</Text>}
                                                    </ProDescriptions.Item>
                                                </ProDescriptions>
                                            </Col>
                                            <Col span={16}>
                                                <ProDescriptions column={1} title="Cover Art" bordered>
                                                    <ProDescriptions.Item>
                                                        {releasePreview.release?.album_cover_filename || <Text type="secondary">N/A</Text>}
                                                    </ProDescriptions.Item>
                                                </ProDescriptions>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                {/* Art Tracks Section */}
                                <Card
                                    type="inner"
                                    title={
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                            <Title level={5} style={{margin: 0}}>Art Track Details</Title>
                                        </div>
                                    }
                                    style={{marginTop: 16}}
                                >
                                    {releasePreview.release?.art_tracks && releasePreview.release.art_tracks.length > 0 ? (
                                        releasePreview.release.art_tracks.map((track, index) => (
                                            <div key={track.id || index} style={{
                                                marginBottom: 8,
                                                paddingBottom: 8,
                                                borderBottom: index < (releasePreview.release.art_tracks.length - 1) ? '1px solid #f0f0f0' : 'none'
                                            }}>
                                                <Row align="middle" gutter={[8, 0]}>
                                                    <Col span={14}>
                                                        <Text strong>{track.title || 'Untitled Track'}</Text>
                                                        {track.subtitle && <Text type="secondary"> ({track.subtitle})</Text>}
                                                    </Col>
                                                    <Col span={10}>
                                                        <Space>
                                                            <Tag>Vol: {track.volume_number || '1'}</Tag>
                                                            <Tag>Track: {track.track_number}</Tag>
                                                            {track.isrc ? (
                                                                <Tag color="success">{track.isrc}</Tag>
                                                            ) : releasePreview.preview_isrcs?.[track.id] ? (
                                                                <Tooltip title="Will be assigned on submission">
                                                                    <Tag color="warning" icon={<WarningOutlined/>}>
                                                                        {releasePreview.preview_isrcs[track.id]}
                                                                    </Tag>
                                                                </Tooltip>
                                                            ) : (
                                                                <Tag color="error">No ISRC</Tag>
                                                            )}
                                                        </Space>
                                                    </Col>
                                                </Row>
                                                <Row gutter={[8, 0]} style={{marginTop: 4}}>
                                                    <Col span={14}>
                                                        <Text type="secondary">Artist: </Text>
                                                        <Text>{track.main_artists?.map(artist =>
                                                            artist.names?.find(name => name.is_default)?.full_name || artist.key_name
                                                        ).join(', ') || <Text type="secondary">N/A</Text>}</Text>
                                                        {track.featuring_artists && track.featuring_artists.length > 0 && (
                                                            <>
                                                                <Text type="secondary"> feat. </Text>
                                                                <Text>{track.featuring_artists.map(artist =>
                                                                    artist.names?.find(name => name.is_default)?.full_name || artist.key_name
                                                                ).join(', ')}</Text>
                                                            </>
                                                        )}
                                                    </Col>
                                                    <Col span={10}>
                                                        <Text type="secondary">Duration: </Text>
                                                        {track.duration ? (
                                                            <Text>
                                                                {Math.floor(track.duration / 1_000_000_000 / 60)}:
                                                                {Math.floor(track.duration / 1_000_000_000 % 60).toString().padStart(2, '0')}
                                                            </Text>
                                                        ) : (
                                                            <Text type="secondary">N/A</Text>
                                                        )}
                                                        <Text type="secondary" style={{marginLeft: 16}}>Type: </Text>
                                                        <Text>{track.track_type || <Text type="secondary">N/A</Text>}</Text>
                                                    </Col>
                                                </Row>
                                                {track.validation_warnings && track.validation_warnings.length > 0 && (
                                                    <Row style={{marginTop: 4}}>
                                                        <Col span={24}>
                                                            <Tag color="warning" icon={<WarningOutlined/>}>
                                                                {track.validation_warnings.length} Warning{track.validation_warnings.length !== 1 ? 's' : ''}
                                                            </Tag>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{textAlign: 'center', padding: '20px'}}>
                                            <Text type="secondary">No tracks available</Text>
                                        </div>
                                    )}
                                </Card>
                            </Panel>
                        );
                    })}
                </Collapse>
            )}

            <div style={{marginTop: 24, textAlign: 'right'}}>
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={processingBatch}
                >
                    Submit Batch
                </Button>
            </div>
        </div>
    );
};

export default VerificationStep;
